import { Observable } from 'rxjs/Observable'
import { combineEpics } from 'redux-observable'

import app from 'containers/app'

import * as api from './api'
import * as actions from './actions'
import { addhttps } from './helpers'

const fetchIntegrationStates = (action$) => {
  return action$
    .ofType(actions.fetchIntegrationStates.toString())
    .pluck('payload')
    .mergeMap(() => {
      actions.fetchIntegrationStatesPending()

      try {
        return api.getIntegrationStates()
          .then((response) => actions.fetchIntegrationStatesSuccess(response))
          .catch(err => {
            return actions.fetchIntegrationStatesFailed(err)
          })
      }
      catch(error) {
        return Observable.of(actions.fetchIntegrationStatesFailed(error))
      }
    })
}

const fetchIntegrationEvents = (action$) => {
  return action$
    .ofType(actions.fetchIntegrationEvents.toString())
    .pluck('payload')
    .switchMap(query => {
      return Observable.fromPromise(api.getIntegrationEventLog(query))
        .map(result => actions.fetchIntegrationEventsSuccess({
          ...result,
          integrationType: query.sourceId
        }))
        .startWith(actions.fetchIntegrationEventsPending({ integrationType: query.sourceId }))
        .catch(error => {
          return Observable.of(actions.fetchIntegrationEventsFailed({ error, integrationType: query.sourceId }))
        })
    })
}

const fetchPaginatedIntegrationEvents = (action$) => {
  return action$
    .ofType(actions.fetchPaginatedIntegrationEvents.toString())
    .pluck('payload')
    .switchMap(({ queryStr, sourceId }) => {
      return Observable.fromPromise(api.getIntegrationEventLogQueryStr(queryStr))
        .map(result => actions.fetchPaginatedIntegrationEventsSuccess({
          ...result,
          integrationType: sourceId
        }))
        .startWith(actions.fetchIntegrationEventsPending({ integrationType: sourceId }))
        .catch(error => {
          return Observable.of(actions.fetchIntegrationEventsFailed({ error, integrationType: sourceId }))
        })
    })
}

const postBusinessEventCsv = (action$) => {
  return action$
    .ofType(actions.postBusinessEventCsv.toString())
    .pluck('payload')
    .mergeMap((payload) => {
      return Observable.fromPromise(api.emailBusinessCsvExport(payload))
      .map(() => {
        app.actions.NotificationActions.pushNotification({
          content: 'We successfully started your CSV export! Check your email for a CSV in a few minutes.'
        })
        return  actions.postBusinessEventCsvSuccess({integrationName: payload.sourceId })
      }).catch(error => { error.json().then((readableError) =>
          app.actions.NotificationActions.pushNotification({
            variant:"red",
            content: readableError.message
          }))
          return Observable.of(actions.postBusinessEventCsvFailed({ integrationName: payload.sourceId, error }))
        })
    })
}

const fetchStripeKeys = action$ => (
  action$
    .ofType(actions.fetchStripeKeys.toString())
    .mergeMap(() => (
      Observable.fromPromise(api.fetchStripeKeys())
        .map(data => actions.fetchStripeKeysSuccess(data))
        .startWith(actions.fetchStripeKeysPending())
        .catch(err => Observable.of(actions.fetchStripeKeysFailed({ err })))
    ))
)

const fetchOutreachStatus = action$ => (
  action$
    .ofType(actions.fetchOutreachStatus.toString())
    .mergeMap(() => (
      Observable.fromPromise(api.fetchOutreachStatus())
        .map(data => actions.fetchOutreachStatusSuccess(data))
        .startWith(actions.fetchOutreachStatusPending())
        .catch(err => Observable.of(actions.fetchOutreachStatusFailed({ err })))
    ))
)

const createAccessToken = action$ => (
  action$
    .ofType(actions.createAccessToken.toString())
    .pluck('payload')
    .mergeMap(({ integrationName, body }) => {
      return Observable.fromPromise(api.createAccessToken(integrationName, body))
        .map(() => actions.createAccessTokenSuccess({ integrationName }))
        .startWith(actions.createAccessTokenPending({ integrationName }))
        .catch(err => {
          return Observable.fromPromise(err.json().then((readableError) => {
            const error = readableError.message
            return actions.createAccessTokenFailed({ err: error, integrationName })
          }))
          .catch((err) => Observable.of(actions.createAccessTokenFailed({ err, integrationName })))
        })
    })
)
const createSandBoxAccessToken = action$ => (
  action$
    .ofType(actions.createSandBoxAccessToken.toString())
    .pluck('payload')
    .mergeMap(({ integrationName, body }) => {
      return Observable.fromPromise(api.createAccessToken("salesforce", body))
        .map(() => actions.createAccessTokenSuccess({ integrationName }))
        .startWith(actions.createAccessTokenPending({ integrationName }))
        .catch(err => {
          return Observable.fromPromise(err.json().then((readableError) => {
            const error = readableError.message
            return actions.createAccessTokenFailed({ err: error, integrationName })
          }))
          .catch((err) => Observable.of(actions.createAccessTokenFailed({ err, integrationName })))
        })
    })
)

const createSlackbotToken = action$ => (
  action$
    .ofType(actions.createSlackbotToken.toString())
    .pluck('payload')
    .mergeMap(({ code, href }) => {
      return Observable.fromPromise(api.createSlackbotToken(code, href))
        .map(() => actions.createAccessTokenSuccess({ integrationName: 'slack' }))
        .startWith(actions.createAccessTokenPending({ integrationName: 'slack' }))
        .catch(err => {
          return Observable.fromPromise(err.json().then((readableError) => {
            const error = readableError.message
            return actions.createAccessTokenFailed({ err: error, integrationName: 'slack' })
          }))
          .catch((err) => Observable.of(actions.createAccessTokenFailed({ err, integrationName: 'slack' })))
        })
    })
)

const updateIntegrationStatus = action$ => (
  action$
    .ofType(actions.updateIntegrationState.toString())
    .pluck('payload')
    .mergeMap(({ integration, connected }) => {
      return Observable.fromPromise(api.updateIntegrationState({ integration, connected }))
        .map((response) => actions.updateIntegrationStateSuccess(response))
        .startWith(actions.updateIntegrationStatePending())
        .catch((err) => Observable.of(actions.updateIntegrationStateFailed({ err })))
    })
)

const setWordpressV2Integration = action$ => (
  action$
    .ofType(actions.setWordpressV2Integration.toString())
    .debounceTime(1000)
    .pluck('payload')
    .mergeMap(({ integration, connected }) => {
      return Observable.fromPromise(api.updateIntegrationState({ integration, connected }))
        .map(response => actions.setWordpressV2IntegrationSuccess(response))
        .startWith(actions.setWordpressV2IntegrationPending())
        .catch(err => Observable.of(actions.setWordpressV2IntegrationFailed({ err })))
    })
)

const setWordpressDomain = action$ => (
  action$
    .ofType(actions.setWordpressDomain.toString())
    .debounceTime(600)
    .pluck('payload')
    .mergeMap(({ rootUrl }) => {
      const url = addhttps(rootUrl)
      return Observable.fromPromise(api.setWordpressDomain(url))
        .map(response => {
          return actions.setWordpressDomainSuccess(response)
        })
        .startWith(actions.setWordpressDomainPending())
        .catch(err => {
          return Observable.of(actions.setWordpressDomainFailed({ err, error: true }))
        })
    })
)

const fetchWordpressDomain = action$ => (
  action$
    .ofType(actions.fetchWordpressDomain.toString())
    .mergeMap(() => {
      return Observable.fromPromise(api.fetchWordpressDomain())
        .map(response => actions.fetchWordpressDomainSuccess(response))
        .startWith(actions.fetchWordpressDomainPending())
        .catch(err => Observable.of(actions.fetchWordpressDomainFailed({ err })))
    })
)

const getLeanDataAuthStatus = action$ => (
  action$
    .ofType(actions.getLeanDataAuthStatus.toString())
    .mergeMap(() => {
        return Observable.fromPromise(api.getLeanDataAuthorizationStatus())
          .map(res => actions.getLeanDataAuthStatusSuccess({ authStatus: res }))
          .startWith(actions.getLeanDataAuthStatusPending())
          .catch(err => Observable.of(actions.getLeanDataAuthStatusFailed({ authStatus: "UNSUCCESSFUL_API_REQUEST" }))) 
    })
  )

export const epic = combineEpics(fetchIntegrationStates,
  fetchOutreachStatus,
  fetchIntegrationEvents,
  fetchPaginatedIntegrationEvents,
  updateIntegrationStatus,
  createAccessToken,
  createSlackbotToken,
  setWordpressV2Integration,
  setWordpressDomain,
  fetchWordpressDomain,
  postBusinessEventCsv,
  createSandBoxAccessToken,
  fetchStripeKeys,
  getLeanDataAuthStatus
)



// WEBPACK FOOTER //
// ./src/scripts/app/modules/integrations/epic.js